import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { UserCog, Save, Users } from "lucide-react";

export default function EAMappings() {
  const [data, setData] = useState({ eas: [], senior_management: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {ea, checked: {sm_id: bool}}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sm-checklist/ea-mappings");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (ea) => {
    const m = {};
    (ea.ea_for || []).forEach((id) => { m[id] = true; });
    setEditing({ ea, checked: m });
  };

  const toggleSm = (id) => {
    setEditing((prev) => ({
      ...prev,
      checked: { ...prev.checked, [id]: !prev.checked[id] },
    }));
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const ids = Object.entries(editing.checked).filter(([_k, v]) => v).map(([k]) => k);
      await api.put(`/sm-checklist/ea-mappings/${editing.ea.id}`, { ea_for: ids });
      toast.success("Mapping saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="ea-mappings-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <UserCog className="h-3 w-3" /> EA / Principal Mapping
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
          EA Principal Assignments
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Assign each Executive Assistant (EA) to the Senior Management principals they support.
          EAs can only log checklist activity / approve requests for principals in their assigned list.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="px-3 py-8 text-center text-slate-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 text-left">EA</th>
                    <th className="px-3 py-2 text-left">Assigned Principals</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.eas.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-400">
                      No EAs found.
                    </td></tr>
                  )}
                  {data.eas.map((ea) => (
                    <tr key={ea.id} className="border-t border-slate-100" data-testid={`ea-row-${ea.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{ea.name}</div>
                        <div className="text-[10px] text-slate-400">{ea.punch_id || "—"}</div>
                      </td>
                      <td className="px-3 py-2">
                        {ea.principals.length === 0 ? (
                          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                            ⚠ No principals assigned — this EA cannot use SM Checklist
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {ea.principals.map((p) => (
                              <span key={p.id} className="text-xs bg-purple-100 text-purple-900 border border-purple-200 rounded px-2 py-0.5">
                                {p.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openEdit(ea)}
                          data-testid={`edit-ea-${ea.id}`}
                        >
                          <Users className="h-3 w-3 mr-1" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data.senior_management.length === 0 && !loading && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-3 text-xs text-amber-800">
            ⚠ No Senior Management users found. Create users with role=&quot;senior_management&quot; first.
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="ea-mapping-dialog">
          <DialogHeader>
            <DialogTitle>Assign Principals — {editing?.ea?.name}</DialogTitle>
            <DialogDescription className="sr-only">
              Select all Senior Management principals this EA supports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {data.senior_management.map((sm) => (
              <div
                key={sm.id}
                className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 border border-transparent hover:border-slate-200 cursor-pointer"
                onClick={() => toggleSm(sm.id)}
                data-testid={`pick-sm-${sm.id}`}
              >
                <Checkbox
                  checked={!!editing?.checked?.[sm.id]}
                  onCheckedChange={() => toggleSm(sm.id)}
                  className="pointer-events-none"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm text-slate-900">{sm.name}</div>
                  <div className="text-[10px] text-slate-400">
                    {sm.designation || "—"} · {sm.punch_id || "—"}
                  </div>
                </div>
              </div>
            ))}
            {data.senior_management.length === 0 && (
              <div className="text-xs text-slate-400 text-center py-4">No SMs available</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="brand-bg brand-bg-hover text-white"
              data-testid="save-ea-mapping-btn"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
